<template>
  <div class="mandate-cell" :style="{ gridColumn: `span ${span}` }">
    <div class="mandate-cell__track">
      <InfoTooltip
        v-for="block in blocks"
        :key="block.key"
        :text="block.tooltip"
        :showIcon="false"
        :style="{ width: block.width }"
      >
        <RouterLink
          v-if="block.dealId !== null"
          :to="dealRoute(block.dealId)"
          class="mandate-cell__segment mandate-cell__segment--linked"
        >
          <span
            class="mandate-cell__escrowed"
            :style="{ width: block.escrowedWidth }"
          ></span>
        </RouterLink>
        <span
          v-else
          class="mandate-cell__segment"
          role="img"
          :aria-label="block.ariaLabel"
        >
          <span
            class="mandate-cell__escrowed"
            :style="{ width: block.escrowedWidth }"
          ></span>
        </span>
      </InfoTooltip>
      <InfoTooltip
        v-if="availableWidth"
        :text="availableLabel"
        :showIcon="false"
        :style="{ width: availableWidth }"
      >
        <span
          class="mandate-cell__available"
          role="img"
          :aria-label="availableLabel"
        ></span>
      </InfoTooltip>
      <span v-if="isEmpty" class="visually-hidden">{{ emptyLabel }}</span>
    </div>
    <!-- Kept when empty: the row centres its cells, so a track without a figure line would sit lower than its neighbours. -->
    <div
      class="mandate-cell__figure"
      :aria-hidden="figure ? undefined : 'true'"
    >
      {{ figure || "\u00a0" }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatDate, formatNumberWithSpaces } from "@/composables/formatUtils";
import InfoTooltip from "@/components/common/InfoTooltip.vue";
import { dealRoute } from "@/forwards2/deals/dealRoute";
import type {
  MandateCell,
  TimelineCommitmentSegment,
} from "@/forwards2/forwardsTypes";
import { computed } from "vue";
import { RouterLink } from "vue-router";
import { useI18n } from "vue-i18n";
import { sumBy } from "lodash-es";

/**
 * One quarter of one mandate row: listed volume as a grey track, commitments stacked on it darkest
 * first, and the still-sellable remainder in the palest tone.
 *
 * Every part carries its figures as a hover tooltip. A commitment's tooltip names its deal and the
 * segment links to it only when the API filled `deal_id`, which it does for staff working as
 * themselves alone; for anyone else the segment is anonymous, its tooltip the figures alone and no
 * link, because a deal name often encodes the buyer.
 *
 * An escrow-remaining row arrives as a cell with only `escrowed` set (the mandate is not the viewer's
 * to list in), and renders as one solid escrow block rather than a blank track.
 */
const props = withDefaults(
  defineProps<{
    /** Null renders an empty track — the API omits cells for quarters with nothing in them. */
    cell: MandateCell | null;
    /** Quarter columns the cell occupies: 2 for a half-year or 4 for a year. */
    span?: number;
    /** Window quarter the cell starts at, for the accessible labels. */
    quarter: string;
    mandateName: string;
  }>(),
  { span: 1 }
);

const { t } = useI18n();

interface CellBlock {
  key: string;
  width: string;
  escrowedWidth: string;
  dealId: number | null;
  tooltip: string;
  ariaLabel: string;
}

/** Everything is measured against the largest of the three totals, so an over-committed period
 *  fills the track instead of overflowing it. */
const denominator = computed(() => {
  const cell = props.cell;
  return cell ? Math.max(cell.listed, cell.committed, cell.escrowed) : 0;
});

const blocks = computed<CellBlock[]>(() => {
  const cell = props.cell;
  if (!cell) return [];
  if (cell.segments.length > 0) {
    // Escrow stranded on a released or cancelled commitment has no live slice to be drawn under,
    // so it gets its own block after the segments; the escrow-only branch below already covers
    // the case with no segments at all.
    const stranded = cell.escrowed - sumBy(cell.segments, "escrowed_mwh");
    return [
      ...cell.segments.map(toBlock),
      ...(stranded > 0 ? [strandedBlock(stranded)] : []),
    ];
  }
  if (cell.committed > 0)
    return [wholeBlock("committed", cell.committed, cell.escrowed)];
  if (cell.escrowed > 0)
    return [wholeBlock("escrowed", cell.escrowed, cell.escrowed)];
  return [];
});

const availableWidth = computed(() => {
  const available = props.cell?.available ?? 0;
  return available > 0 ? percent(available) : "";
});

const isEmpty = computed(
  () => blocks.value.length === 0 && availableWidth.value === ""
);

/** A span cell's figures cover its wider period, so its labels name that period, not its first quarter
 *  (C-FIX25-03). Falls back to the grid position when there is no cell to read a period off. */
const periodLabel = computed(() => props.cell?.period_label ?? props.quarter);

const availableLabel = computed(() =>
  [
    t("forwards.mandates.cell_available", {
      mandate: props.mandateName,
      quarter: periodLabel.value,
      value: formatNumberWithSpaces(props.cell?.available ?? 0),
    }),
    t("forwards.mandates.cell_sellable_now", {
      value: formatNumberWithSpaces(props.cell?.sellable_now ?? 0),
    }),
  ].join(" · ")
);

const emptyLabel = computed(() =>
  t("forwards.mandates.cell_empty", {
    mandate: props.mandateName,
    quarter: periodLabel.value,
  })
);

const figure = computed(() => {
  const cell = props.cell;
  if (!cell) return "";
  if (cell.committed > 0) {
    return t("forwards.mandates.cell_figure", {
      committed: formatNumberWithSpaces(cell.committed),
      listed: formatNumberWithSpaces(cell.listed),
    });
  }
  if (cell.listed > 0) return formatNumberWithSpaces(cell.listed);
  if (cell.escrowed > 0)
    return t("forwards.mandates.cell_escrowed_figure", {
      value: formatNumberWithSpaces(cell.escrowed),
    });
  return "";
});

function percent(value: number): string {
  return denominator.value > 0 ? `${(value / denominator.value) * 100}%` : "0%";
}

function escrowedShare(volume: number, escrowed: number): string {
  return volume > 0 ? `${Math.min(100, (escrowed / volume) * 100)}%` : "0%";
}

function wholeBlock(key: string, volume: number, escrowed: number): CellBlock {
  return anonymousBlock({
    key,
    width: percent(volume),
    escrowedWidth: escrowedShare(volume, escrowed),
    ariaLabel: t("forwards.mandates.cell_committed", {
      mandate: props.mandateName,
      quarter: periodLabel.value,
      value: formatNumberWithSpaces(volume),
      escrowed: formatNumberWithSpaces(escrowed),
    }),
  });
}

function strandedBlock(volume: number): CellBlock {
  return anonymousBlock({
    key: "stranded",
    width: percent(volume),
    escrowedWidth: "100%",
    ariaLabel: t("forwards.mandates.cell_stranded", {
      mandate: props.mandateName,
      quarter: periodLabel.value,
      value: formatNumberWithSpaces(volume),
    }),
  });
}

function anonymousBlock(
  block: Omit<CellBlock, "dealId" | "tooltip">
): CellBlock {
  return { ...block, dealId: null, tooltip: block.ariaLabel };
}

function toBlock(segment: TimelineCommitmentSegment, index: number): CellBlock {
  const shared = {
    key: `${segment.commitment_id}-${segment.slice_id ?? index}`,
    width: percent(segment.volume_mwh),
    escrowedWidth: escrowedShare(segment.volume_mwh, segment.escrowed_mwh),
    ariaLabel: t("forwards.mandates.cell_committed", {
      mandate: props.mandateName,
      quarter: periodLabel.value,
      value: formatNumberWithSpaces(segment.volume_mwh),
      escrowed: formatNumberWithSpaces(segment.escrowed_mwh),
    }),
  };

  if (segment.deal_id === null) return anonymousBlock(shared);

  return {
    ...shared,
    dealId: segment.deal_id,
    tooltip: t("forwards.mandates.cell_deal_tooltip", {
      deal: segment.deal_name ?? "",
      value: formatNumberWithSpaces(segment.volume_mwh),
      escrowed: formatNumberWithSpaces(segment.escrowed_mwh),
      status: t(`forwards.common.status.${segment.deal_status}`),
      window: segment.window_end
        ? formatDate(segment.window_end)
        : t("forwards.common.no_window_end"),
    }),
  };
}
</script>

<style scoped lang="scss">
@use "@/forwards2/forwards-colors" as colors;

.mandate-cell {
  min-width: 0;
  padding: 0 0.125rem;
}

.mandate-cell__track {
  display: flex;
  align-items: stretch;
  height: 1.25rem;
  border-radius: 3px;
  background: colors.$forwards-track;
  overflow: hidden;
}

.mandate-cell__segment {
  display: block;
  width: 100%;
  height: 100%;
  background: colors.$forwards-committed;
}

.mandate-cell__segment--linked:hover {
  outline: 2px solid var(--phoenix-primary);
  outline-offset: -2px;
}

.mandate-cell__escrowed {
  display: block;
  height: 100%;
  background: colors.$forwards-escrowed;
}

.mandate-cell__available {
  display: block;
  height: 100%;
  background: colors.$forwards-available;
}

.mandate-cell__figure {
  font-size: 0.625rem;
  color: var(--phoenix-gray-600);
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

// InfoTooltip wraps its slot in an inline span; make it a block so the width binding applies, and
// keep a sliver of a tiny part wide enough to hover.
:deep(.info-tooltip-trigger) {
  display: block;
  min-width: 2px;
}
</style>
